// Sticky Add to Cart Button Functionality
class StickyAddToCart {
  constructor() {
    this.stickyButton = document.getElementById('sticky-add-to-cart');
    this.mainAddToCartButton = document.querySelector('.product-form [name="add"]');
    this.productForm = document.querySelector('.product-form');
    this.isVisible = false;
    this.isMobile = window.innerWidth <= 768;
    
    if (this.stickyButton) {
      this.init();
    }
  }

  init() {
    this.setupScrollListener();
    this.setupFormSync();
    this.setupMobileCartDrawer();
    
    // Update mobile status on resize
    window.addEventListener('resize', () => {
      this.isMobile = window.innerWidth <= 768;
    });
  }

  setupScrollListener() {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          this.checkVisibility();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    
    // Initial check
    this.checkVisibility();
  }

  checkVisibility() {
    if (!this.mainAddToCartButton) return;

    const mainButtonRect = this.mainAddToCartButton.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    
    // Show sticky button when main button is out of view
    const shouldShow = mainButtonRect.bottom < 0 || mainButtonRect.top > windowHeight;
    
    if (shouldShow && !this.isVisible) {
      this.showStickyButton();
    } else if (!shouldShow && this.isVisible) {
      this.hideStickyButton();
    }
  }

  showStickyButton() {
    this.stickyButton.style.display = 'block';
    // Small delay to ensure display is applied before adding class
    setTimeout(() => {
      this.stickyButton.classList.add('show');
    }, 10);
    this.isVisible = true;
  }

  hideStickyButton() {
    this.stickyButton.classList.remove('show');
    // Hide after transition completes
    setTimeout(() => {
      if (!this.stickyButton.classList.contains('show')) {
        this.stickyButton.style.display = 'none';
      }
    }, 300);
    this.isVisible = false;
  }

  setupFormSync() {
    const mainForm = this.productForm;
    const stickyForm = this.stickyButton?.querySelector('.sticky-product-form');
    
    if (!mainForm || !stickyForm) return;

    // Sync variant selection from main form to sticky form
    const mainVariantInput = mainForm.querySelector('[name="id"]');
    const stickyVariantInput = stickyForm.querySelector('[name="id"]');
    
    if (mainVariantInput && stickyVariantInput) {
      const syncVariant = () => {
        stickyVariantInput.value = mainVariantInput.value;
        
        // Update button state based on availability
        const stickyButton = stickyForm.querySelector('[name="add"]');
        const mainButton = mainForm.querySelector('[name="add"]');
        
        if (mainButton && stickyButton) {
          stickyButton.disabled = mainButton.disabled;
          const buttonText = stickyButton.querySelector('span');
          const mainButtonText = mainButton.querySelector('span');
          
          if (buttonText && mainButtonText) {
            buttonText.textContent = mainButtonText.textContent;
          }
        }
        
        // Update price in sticky button
        this.updateStickyPrice();
      };

      // Listen for variant changes
      mainVariantInput.addEventListener('change', syncVariant);
      
      // Listen for custom variant change events
      document.addEventListener('variant:change', syncVariant);
      
      // Listen for variant picker changes (common in Shopify themes)
      const variantPickers = mainForm.querySelectorAll('variant-radios, variant-selects, [data-variant-picker]');
      variantPickers.forEach(picker => {
        picker.addEventListener('change', syncVariant);
      });
      
      // Listen for option changes
      const optionInputs = mainForm.querySelectorAll('input[name^="options"], select[name^="options"]');
      optionInputs.forEach(input => {
        input.addEventListener('change', syncVariant);
      });
      
      // Use MutationObserver to watch for variant input value changes
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
            syncVariant();
          }
        });
      });
      
      observer.observe(mainVariantInput, {
        attributes: true,
        attributeFilter: ['value']
      });
      
      // Initial sync
      syncVariant();
    }
  }

  updateStickyPrice() {
    const mainPrice = this.productForm?.querySelector('.price');
    const stickyPrice = this.stickyButton?.querySelector('.sticky-product-price');
    
    if (mainPrice && stickyPrice) {
      // Copy the price from main form to sticky button
      const mainPriceClone = mainPrice.cloneNode(true);
      const priceElements = mainPriceClone.querySelectorAll('.price__regular, .price__sale, .price__compare');
      
      let priceHTML = '';
      priceElements.forEach(priceEl => {
        if (priceEl.classList.contains('price__sale')) {
          priceHTML += `<span class="sticky-price-sale">${priceEl.textContent}</span>`;
        } else if (priceEl.classList.contains('price__compare')) {
          priceHTML += `<span class="sticky-price-compare">${priceEl.textContent}</span>`;
        } else if (priceEl.classList.contains('price__regular')) {
          priceHTML += `<span class="sticky-price-regular">${priceEl.textContent}</span>`;
        }
      });
      
      if (priceHTML) {
        stickyPrice.innerHTML = priceHTML;
      }
    }
  }


  setupMobileCartDrawer() {
    const stickyForm = this.stickyButton?.querySelector('.sticky-product-form');
    const stickyAddButton = stickyForm?.querySelector('[name="add"]');
    
    if (!stickyAddButton) return;

    stickyAddButton.addEventListener('click', (e) => {
      if (this.isMobile) {
        e.preventDefault();
        this.handleMobileAddToCart();
      }
      // On desktop, let the form submit normally
    });
  }

  async handleMobileAddToCart() {
    const stickyForm = this.stickyButton.querySelector('.sticky-product-form form');
    const formData = new FormData(stickyForm);
    const stickyAddButton = stickyForm.querySelector('[name="add"]');
    
    // Show loading state
    stickyAddButton.setAttribute('aria-disabled', true);
    stickyAddButton.classList.add('loading');
    
    try {
      // Add to cart via AJAX
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const item = await response.json();
        
        // Update cart count and contents
        await this.updateCartCount();
        
        // Refresh cart drawer contents before opening
        await this.refreshCartDrawer();
        
        // Small delay to ensure content is updated
        setTimeout(() => {
          this.openCartDrawer();
        }, 100);
        
        // Show success feedback
        this.showAddToCartSuccess();
        
      } else {
        throw new Error('Failed to add to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showAddToCartError();
    } finally {
      // Remove loading state
      stickyAddButton.removeAttribute('aria-disabled');
      stickyAddButton.classList.remove('loading');
    }
  }

  async updateCartCount() {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart.js');
      const cart = await response.json();
      
      const cartCountElements = document.querySelectorAll('.cart-count-bubble span, [data-cart-count]');
      cartCountElements.forEach(element => {
        element.textContent = cart.item_count;
      });
      
      return cart;
    } catch (error) {
      console.error('Error updating cart count:', error);
      return null;
    }
  }

  async refreshCartDrawer() {
    try {
      // Method 1: Try to refresh cart drawer contents via AJAX
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && cartDrawer.getSectionsToRender) {
        await cartDrawer.getSectionsToRender().forEach(async (section) => {
          const response = await fetch(`${window.Shopify.routes.root}?section_id=${section.id}`);
          const html = await response.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const newContent = doc.querySelector(section.selector);
          if (newContent) {
            document.querySelector(section.selector).innerHTML = newContent.innerHTML;
          }
        });
        return;
      }

      // Method 2: Try to trigger cart drawer refresh
      const cartNotification = document.querySelector('cart-notification');
      if (cartNotification && cartNotification.renderContents) {
        await cartNotification.renderContents();
        return;
      }

      // Method 3: Fetch and update cart drawer HTML directly
      const response = await fetch(window.Shopify.routes.root + '?section_id=cart-drawer');
      if (response.ok) {
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newCartDrawer = doc.querySelector('cart-drawer, .cart-drawer');
        const currentCartDrawer = document.querySelector('cart-drawer, .cart-drawer');
        
        if (newCartDrawer && currentCartDrawer) {
          currentCartDrawer.innerHTML = newCartDrawer.innerHTML;
        }
      }

      // Method 4: Dispatch cart update event
      document.dispatchEvent(new CustomEvent('cart:updated'));
      
    } catch (error) {
      console.error('Error refreshing cart drawer:', error);
    }
  }

  openCartDrawer() {
    // Try different methods to open cart drawer based on theme implementation
    
    // Method 1: Look for cart drawer toggle button
    const cartDrawerToggle = document.querySelector('[data-cart-drawer-toggle], .cart-drawer-toggle, #cart-icon-bubble');
    if (cartDrawerToggle) {
      cartDrawerToggle.click();
      return;
    }

    // Method 2: Look for cart drawer element and show it directly
    const cartDrawer = document.querySelector('cart-drawer, .cart-drawer, #CartDrawer');
    if (cartDrawer) {
      if (cartDrawer.classList.contains('cart-drawer')) {
        cartDrawer.classList.add('active', 'animate');
      } else if (cartDrawer.open !== undefined) {
        cartDrawer.open();
      }
      return;
    }

    // Method 3: Dispatch custom event that the theme might listen to
    document.dispatchEvent(new CustomEvent('cart:open-drawer'));
    
    // Method 4: Try to trigger cart notification
    const cartNotification = document.querySelector('cart-notification');
    if (cartNotification && cartNotification.renderContents) {
      cartNotification.renderContents();
    }
  }

  showAddToCartSuccess() {
    // Create a temporary success message
    const successMessage = document.createElement('div');
    successMessage.className = 'sticky-cart-success';
    successMessage.innerHTML = '✓ Added to cart!';
    successMessage.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #28a745;
      color: white;
      padding: 12px 20px;
      border-radius: 6px;
      z-index: 10000;
      font-size: 14px;
      font-weight: 600;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      transform: translateX(100%);
      transition: transform 0.3s ease;
    `;
    
    document.body.appendChild(successMessage);
    
    // Animate in
    setTimeout(() => {
      successMessage.style.transform = 'translateX(0)';
    }, 10);
    
    // Remove after 3 seconds
    setTimeout(() => {
      successMessage.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (successMessage.parentNode) {
          successMessage.parentNode.removeChild(successMessage);
        }
      }, 300);
    }, 3000);
  }

  showAddToCartError() {
    // Create a temporary error message
    const errorMessage = document.createElement('div');
    errorMessage.className = 'sticky-cart-error';
    errorMessage.innerHTML = '✗ Failed to add to cart';
    errorMessage.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #dc3545;
      color: white;
      padding: 12px 20px;
      border-radius: 6px;
      z-index: 10000;
      font-size: 14px;
      font-weight: 600;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      transform: translateX(100%);
      transition: transform 0.3s ease;
    `;
    
    document.body.appendChild(errorMessage);
    
    // Animate in
    setTimeout(() => {
      errorMessage.style.transform = 'translateX(0)';
    }, 10);
    
    // Remove after 4 seconds
    setTimeout(() => {
      errorMessage.style.transform = 'translateX(100%)';
      setTimeout(() => {
        if (errorMessage.parentNode) {
          errorMessage.parentNode.removeChild(errorMessage);
        }
      }, 300);
    }, 4000);
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new StickyAddToCart();
});

// Re-initialize if content changes (for AJAX updates)
document.addEventListener('shopify:section:load', () => {
  new StickyAddToCart();
});
